// import { Component } from '@theme/component';
// import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

// /**
//  * A custom element that renders a media gallery.
//  *
//  * @typedef {object} Refs
//  * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
//  * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
//  * @property {HTMLElement[]} [media] - The media elements.
//  *
//  * @extends Component<Refs>
//  */
// export class MediaGallery extends Component {
//   connectedCallback() {
//     super.connectedCallback();

//     const { signal } = this.#controller;
//     const target = this.closest('.shopify-section, dialog');

//     target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
//     this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
//       signal,
//     });
//   }

//   #controller = new AbortController();

//   disconnectedCallback() {
//     super.disconnectedCallback();

//     this.#controller.abort();
//   }

//   /**
//    * Handles a variant update event by replacing the current media gallery with a new one.
//    *
//    * @param {VariantUpdateEvent} event - The variant update event.
//    */
//   #handleVariantUpdate = (event) => {
//     const source = event.detail.data.html;
//     const updatedVariant = event.detail.resource;
    
//     if (!source) return;
    
//     const newMediaGallery = source.querySelector('media-gallery');
//     newMediaGallery.querySelectorAll("slideshow-slide.product-media-container").forEach((node) => {
//       node.removeAttribute("hidden");
//     });
//     let filteredOutNodes = newMediaGallery.querySelectorAll(`slideshow-slide.product-media-container:not(:has(img[alt="${updatedVariant.option1}"]))`);

//     // Check if any images actually have the matching alt attribute
//     let matchingNodes = newMediaGallery.querySelectorAll(`slideshow-slide.product-media-container:has(img[alt="${updatedVariant.option1}"])`);
    
//     // Only hide non-matching images if there are actually matching images
//     if (matchingNodes.length > 0) {
//       filteredOutNodes.forEach((node) => {
//         node.setAttribute("hidden", "");
//       });
//     }
//     // If no matching images found, leave all images visible (don't hide any)

//     if (!newMediaGallery) return;

//     this.replaceWith(newMediaGallery);

//     const newSlideshow = newMediaGallery.querySelector('slideshow-component');
    
//     if (newSlideshow && newSlideshow.refresh) {
//       queueMicrotask(() => {
//         newSlideshow.refresh();
//       });
//     }
//     document.querySelector("media-gallery slideshow-controls").style.display = "none";
//   };

//   /**
//    * Handles the 'zoom-media:selected' event.
//    * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
//    */
//   #handleZoomMediaSelected = async (event) => {
//     this.slideshow?.select(event.detail.index, undefined, { animate: false });
//   };

//   /**
//    * Zooms the media gallery.
//    *
//    * @param {number} index - The index of the media to zoom.
//    * @param {PointerEvent} event - The pointer event.
//    */
//   zoom(index, event) {
//     this.refs.zoomDialogComponent?.open(index, event);
//   }

//   get slideshow() {
//     return this.refs.slideshow;
//   }

//   get media() {
//     return this.refs.media;
//   }

//   get presentation() {
//     return this.dataset.presentation;
//   }
// }

// if (!customElements.get('media-gallery')) {
//   customElements.define('media-gallery', MediaGallery);
// }


import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    const updatedVariant = event.detail.resource;
    
    if (!source) return;
    
    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    // Get all slides in the new gallery
    const allSlides = Array.from(newMediaGallery.querySelectorAll("slideshow-slide.product-media-container"));
    
    // Get the variant's featured media ID
    const variantFeaturedMediaId = updatedVariant?.featured_media?.id 
      ? String(updatedVariant.featured_media.id) 
      : null;
    
    // Get variant option values (e.g., "Silver", "Gun Metal Black")
    // @ts-ignore - option1, option2, option3 may exist on variant object
    const selectedVariantOptions = [
      updatedVariant?.option1,
      updatedVariant?.option2,
      updatedVariant?.option3
    ].filter(Boolean).map(opt => String(opt).toLowerCase());
    
    // Get variant picker to find all other variant options
    const variantPicker = source.querySelector('variant-picker') || 
                          document.querySelector('variant-picker') ||
                          newMediaGallery.closest('.shopify-section')?.querySelector('variant-picker');
    
    // Get ALL variant option values from the variant picker (both selected and others)
    const allVariantOptions = new Set();
    const otherVariantOptions = new Set();
    
    // Method 1: Get from variant picker - check both inputs and selects
    if (variantPicker) {
      // Get from radio/checkbox inputs (value attribute contains the option text)
      const allInputs = variantPicker.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      allInputs.forEach(input => {
        const variantId = input.getAttribute('data-variant-id');
        const value = input.getAttribute('value')?.toLowerCase().trim();
        
        if (value && value.length > 0) {
          allVariantOptions.add(value);
          // Add to other variants if not selected
          if (variantId && String(variantId) !== String(updatedVariant?.id)) {
            otherVariantOptions.add(value);
          }
        }
      });
      
      // Get from select options
      const allSelects = variantPicker.querySelectorAll('select');
      allSelects.forEach(select => {
        const options = select.querySelectorAll('option');
        options.forEach(option => {
          const variantId = option.getAttribute('data-variant-id');
          const value = option.getAttribute('value')?.toLowerCase().trim();
          
          if (value && value.length > 0) {
            allVariantOptions.add(value);
            // Add to other variants if not selected
            if (variantId && String(variantId) !== String(updatedVariant?.id)) {
              otherVariantOptions.add(value);
            }
          }
        });
      });
    }
    
    // Method 2: Also add selected variant options directly (from variant object)
    selectedVariantOptions.forEach(opt => {
      if (opt && opt.length > 0) {
        allVariantOptions.add(opt);
      }
    });
    
    // Method 3: Also check the source HTML for variant options
    if (source) {
      const sourceVariantPicker = source.querySelector('variant-picker');
      if (sourceVariantPicker) {
        const sourceInputs = sourceVariantPicker.querySelectorAll('input[type="radio"], input[type="checkbox"], select option');
        sourceInputs.forEach(input => {
          const value = input.getAttribute('value')?.toLowerCase().trim();
          if (value && value.length > 0) {
            allVariantOptions.add(value);
          }
        });
      }
    }
    
    // Debug: Log variant options
    console.log('Selected variant options:', selectedVariantOptions);
    console.log('All variant options:', Array.from(allVariantOptions));
    console.log('Other variant options:', Array.from(otherVariantOptions));
    
    // Filter slides: PRIMARY METHOD - Use alt text to identify variant images
    allSlides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) {
        // No image element - show it (might be video or other media)
        slide.removeAttribute("hidden");
        return;
      }
      
      const altText = (img.alt || '').toLowerCase().trim();
      const src = (img.src || '').toLowerCase();
      const slideId = slide.getAttribute('slide-id');
      
      // PRIMARY: Check if image alt text matches variant options
      let matchesSelectedVariant = false;
      let matchesOtherVariant = false;
      let isVariantSpecificImage = false;
      let matchedOption = null;
      
      // First, check if this image is variant-specific by checking against ALL variant options
      // Use flexible matching: exact match, contains, or word boundary matching
      for (const option of allVariantOptions) {
        // Normalize both strings for comparison
        const normalizedAlt = altText.replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
        const normalizedOption = option.replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
        
        // Check multiple matching strategies
        const exactMatch = normalizedAlt === normalizedOption;
        const containsMatch = normalizedAlt.includes(normalizedOption) || normalizedOption.includes(normalizedAlt);
        const wordMatch = normalizedAlt.split(/\s+/).some(word => normalizedOption.includes(word)) ||
                         normalizedOption.split(/\s+/).some(word => normalizedAlt.includes(word));
        const srcMatch = src.includes(option);
        
        if (exactMatch || containsMatch || wordMatch || srcMatch) {
          isVariantSpecificImage = true;
          matchedOption = option;
          
          // Check if it matches selected variant
          if (selectedVariantOptions.includes(option)) {
            matchesSelectedVariant = true;
            console.log(`✓ Slide matches SELECTED variant: alt="${altText}", option="${option}"`);
          } else {
            matchesOtherVariant = true;
            console.log(`✗ Slide matches OTHER variant: alt="${altText}", option="${option}"`);
          }
          break;
        }
      }
      
      // If no match found, log it for debugging
      if (!isVariantSpecificImage && altText) {
        console.log(`? Slide alt text doesn't match any variant: alt="${altText}", available options:`, Array.from(allVariantOptions));
      }
      
      // BACKUP: Check if slide ID matches variant's featured media
      if (!matchesSelectedVariant && variantFeaturedMediaId && slideId) {
        if (String(slideId) === variantFeaturedMediaId) {
          matchesSelectedVariant = true;
          isVariantSpecificImage = true;
          console.log(`Slide matches featured media ID: ${slideId}`);
        }
      }
      
      // Decision logic:
      // 1. If it's a variant-specific image and matches selected variant → show
      // 2. If it's a variant-specific image and matches other variants → hide
      // 3. If it's NOT variant-specific (general product image) → show
      
      if (isVariantSpecificImage) {
        // This is a variant-specific image
        if (matchesSelectedVariant && !matchesOtherVariant) {
          // Matches selected variant → show
          slide.removeAttribute("hidden");
          console.log(`Showing slide (selected variant): alt="${altText}"`);
        } else {
          // Matches other variant or ambiguous → hide
          slide.setAttribute("hidden", "");
          console.log(`Hiding slide (other variant): alt="${altText}"`);
        }
      } else {
        // Not variant-specific → it's a general product image → show
        slide.removeAttribute("hidden");
        console.log(`Showing slide (general): alt="${altText}"`);
      }
    });

    // Replace the gallery
    this.replaceWith(newMediaGallery);

    // Refresh the slideshow AFTER replacement
    setTimeout(() => {
      const slideshow = newMediaGallery.querySelector('slideshow-component');
      
      if (slideshow && 'refresh' in slideshow && typeof slideshow.refresh === 'function') {
        // Force refresh to update slides array
        slideshow.refresh();
        
        // Wait a bit for refresh to complete, then update
        setTimeout(() => {
          // @ts-ignore - slides property exists on slideshow component
          const slides = slideshow.slides || [];
          const visibleSlides = Array.isArray(slides) ? Array.from(slides).filter(
            slide => slide && !slide.hasAttribute('hidden')
          ) : [];
          
          // Select first visible slide
          if (visibleSlides.length > 0 && 'select' in slideshow && typeof slideshow.select === 'function') {
            const firstVisibleSlide = visibleSlides[0];
            if (firstVisibleSlide && Array.isArray(slides)) {
              const firstVisibleIndex = slides.indexOf(firstVisibleSlide);
              if (firstVisibleIndex >= 0) {
                // @ts-ignore - select method exists on slideshow component
                slideshow.select(firstVisibleIndex, undefined, { animate: false });
              }
            }
          }
          
          // Update controls visibility
          const controls = newMediaGallery.querySelector("slideshow-controls");
          const arrows = newMediaGallery.querySelector("slideshow-arrows");
          const visibleCount = visibleSlides.length;
          
          if (controls && controls instanceof HTMLElement) {
            controls.style.display = visibleCount <= 1 ? "none" : "";
          }
          if (arrows && arrows instanceof HTMLElement) {
            arrows.style.display = visibleCount <= 1 ? "none" : "";
          }
        }, 50);
      }
    }, 0);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
   